import JEN_HowSFWork_Animation from '@/assets/animation/JEN_HowSFWork-Animation_Web--v04.json';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/Accordion';
import { PauseIcon, PlayIcon } from '@/components/Icons';
import StyleFilterDetailCard from '@/components/StyleFilterDetailCard/StyleFilterDetailCard';
import { TrackList } from '@/components/TrackList';
import { useStyleFilter } from '@/pages/Home/Generator/hooks/useStyleFilter';
import { useBuyStyleFilter } from '@/pages/StyleFilterMarket/hooks/useBuyStyleFilter';
import BackLastPage from '@/pages/StyleFilterMarket/mobile/components/BackLastPage';
import StrengthToggle from '@/pages/StyleFilterMarket/mobile/components/StrengthSelector/StrengthToggle';
import { getStyleFilterGroupDetail } from '@/service/filter';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useStyleFilterStore, useTrackLists } from '@/state/style-filter-list-store';
import { cn } from '@/utils';
import { useRequest } from 'ahooks';
import Lottie from 'lottie-react';
import { ArtistLink } from '../ArtistLink';
import { checkAuthAndRedirect } from '@/hooks/useAuthCheck';

interface StyleFilterDetailProps {
  id: number;
}

const StyleFilterDetailDialogContent = ({ id }: StyleFilterDetailProps) => {
  const { data } = useRequest(() => getStyleFilterGroupDetail(id));
  const { marketAudioListData } = useTrackLists();
  const { postStyleFilterOrderRun } = useStyleFilter();
  const { play, playAudioInfo, isPlaying, pause, stop, currentTime } = useAudioPlayerStore();
  const isCurrentPlaying = playAudioInfo?.id === String(id) && isPlaying;
  const { selectedFiltersState } = useStyleFilterStore();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { selectedFilter, selectedValue, handleSwitch, handleBuy } = useBuyStyleFilter(data?.data || { sf_list: [] });

  if (!data?.data || !selectedFilter) {
    return null;
  }

  const sfDetail = data?.data;

  const forceUpdateKey = id && selectedFiltersState[id] ? selectedFiltersState[id].value : undefined;

  return (
    <div className="bg-jen-background-tertiary">
      <BackLastPage className="px-6 pt-6" />
      <div className="px-12">
        <div className="pt-[60px]">
          <div className="flex mb-8 mx-[60px] gap-[64px] justify-center">
            <div className="flex flex-col">
              <div className="w-[400px] rounded-2xl">
                <StyleFilterAlbum
                  sfDetail={sfDetail}
                  handleSwitch={handleSwitch}
                  defaultValue={selectedValue === 0 ? 'LOW' : 'HIGH'}
                  forceUpdateKey={forceUpdateKey}
                />
              </div>
              {/* <img src={AwardSrc} className="w-[225px] h-[20px] mx-auto mb-7" /> */}
            </div>
            <div className="flex flex-col gap-[32px] max-w-[480px] [calc(100%-351px)] box-content mt-[24px]">
              <div className="header">
                <div className="flex items-center">
                  <ArtistLink
                    artist={sfDetail}
                    textClassName="text-[#111112] text-[18px]"
                    avatarClassName="w-8 h-8 border-jen-interactive-primary"
                  />
                </div>
                <div className="text-[#111112] text-[48px] tracking-[-3.2px] leading-[48px] my-3">{sfDetail.name}</div>
                <div
                  className="flex text-jen-interactive-primary items-center cursor-pointer hover:opacity-90"
                  onClick={() => {
                    if (selectedFilter?.audio) {
                      stop();
                      if (isCurrentPlaying) {
                        pause();
                      } else {
                        play({
                          url: selectedFilter.key === 'high' ? sfDetail.sf_list[1].audio : sfDetail.sf_list[0].audio,
                          name: sfDetail.name,
                          id: String(sfDetail.id),
                        });
                      }
                    }
                  }}
                >
                  <div
                    className="flex justify-center items-center w-9 h-9 rounded-full"
                    style={{
                      background: ' var(--Colors-Orange-10, rgba(255, 91, 39, 0.10))',
                    }}
                  >
                    {isCurrentPlaying ? <PauseIcon width={14} fillColor="#ff5b27" /> : <PlayIcon width={14} fillColor="#ff5b27" />}
                    {isPlaying && (
                      <svg className="absolute top-0 left-0 w-12 h-12 -rotate-90" viewBox="0 0 48 48">
                        <circle
                          cx="24"
                          cy="24"
                          r="22"
                          fill="none"
                          stroke="#FF5B27"
                          strokeWidth="2"
                          strokeDasharray={`${(currentTime / (selectedFilter?.audio_duration || 0)) * (2 * 3.14 * 22)} ${2 * 3.14 * 22}`}
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="ml-2">
                    Preview StyleFilter<sup className="top-[-6px] text-[6px]">TM</sup>
                  </span>
                  <span className="ml-1">Track</span>
                </div>
              </div>
              <div className="self-stretch justify-start text-[#111112] text-[20px] leading-[26px] tracking-[-3%]">
                Choose between low or high strength to infuse more or less of the song style into your jeneration:
              </div>
              <div className="flex flex-col gap-[12px]">
                <StrengthToggle
                  options={sfDetail?.sf_list.map((item) => ({
                    label: item.name,
                    price: Number(item.price),
                    value: item.id.toString(),
                  }))}
                  defaultValue={selectedFilter.id.toString()}
                  forceUpdateKey={forceUpdateKey}
                  onChange={(value) => {
                    const selectedItem = sfDetail.sf_list.find((item) => item.id.toString() === value);
                    const strengthValue = selectedItem?.key === 'high' ? 1 : 0;
                    handleSwitch({
                      value: strengthValue,
                      id: Number(value),
                    });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    checkAuthAndRedirect();
                    postStyleFilterOrderRun({
                      orders: [
                        {
                          id: selectedFilter.id,
                        },
                      ],
                    });
                  }}
                  className={cn(
                    'w-full cursor-pointer leading-4 text-jen-content-primary-contrast rounded-md bg-jen-interactive-primary py-[14px] px-[16px] hover:opacity-90',
                  )}
                >
                  Purchase
                </button>
                <div className="justify-start text-jen-content-tertiary text-base font-normal leading-none">
                  90 Min of {selectedFilter?.key === 'high' ? 'High' : 'Low'} Strength Jeneration
                </div>
              </div>
              <div className="detail">
                <StyleFilterAccordion />
              </div>
            </div>
          </div>
          <div className="pb-6">
            <section className="bg-white p-8 bg-jen-background-secondary border border-jen-border-background rounded-2xl">
              <h2 className="text-[32px] text-[#48484b] tracking-[-0.96px] leading-10 mb-6">
                Tracks Jenerated with
                <div>
                  <span>Imogen Heap </span>StyleFilters™
                </div>
              </h2>
              <TrackList
                trackListData={marketAudioListData[`${sfDetail.sf_list[0].code}`]}
                label={{ title: 'Title', creator: 'User', filter: 'StyleFilter' }}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

const StyleFilterAlbum = ({
  sfDetail,
  handleSwitch,
  defaultValue = 'LOW',
  forceUpdateKey,
}: {
  sfDetail: FilterGroup;
  handleSwitch: ({ value, id }: { value: number; id: number | undefined }) => void;
  defaultValue?: 'LOW' | 'HIGH' | 'NONE';
  forceUpdateKey?: number;
}) => {
  return (
    <StyleFilterDetailCard
      onRemove={() => undefined}
      onUse={() => undefined}
      title={sfDetail?.artist}
      content={sfDetail?.name}
      sfId={sfDetail?.id}
      background={sfDetail?.album_cover}
      showButtons={false}
      disabled={true}
      isMarket={true}
      onSwitch={handleSwitch}
      defaultValue={defaultValue}
      className="w-full h-full"
      fontSize={18}
      rightTagClassname="scale-[1.5] right-4 top-8"
      coverStyle={{
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '336px',
        height: '546px',
        marginBottom: '25px',
        boxShadow: `
  0.6px 0.6px 0.6px rgba(0, 0, 0, 0.019),
  1.4px 1.3px 1.5px rgba(0, 0, 0, 0.028),
  2.6px 2.5px 2.9px rgba(0, 0, 0, 0.035),
  4.7px 4.5px 5.2px rgba(0, 0, 0, 0.041),
  8.8px 8.4px 9.3px rgba(0, 0, 0, 0.05),
  21px 20px 21px rgba(0, 0, 0, 0.07)
          `,
      }}
    />
  );
};

const StyleFilterAccordion = () => {
  return (
    <Accordion type="multiple" className="w-full">
      <AccordionItem value="item-1" className=" border-b border-t border-[#CECFD3] py-4">
        <AccordionTrigger className=" text-[#48484b]">
          <div>
            How StyleFilters<sup className=" inline top-[-6px] text-[6px]">TM</sup> Work
          </div>
        </AccordionTrigger>
        <AccordionContent className="mt-[24px]">
          <Lottie animationData={JEN_HowSFWork_Animation} className="" />
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="item-2" className="border-b border-[#CECFD3] py-4">
        <AccordionTrigger className=" text-[#48484b]">
          <div>
            StyleFilters<sup className=" inline top-[-6px] text-[6px]">TM</sup> Jenerated Usage
          </div>
        </AccordionTrigger>
        <AccordionContent className=" text-jen-content-tertiary text-[16px] leading-[19px] mt-[16px]">
          Tracks created with StyleFilters™ are fully owned by the user—ready to be used, released, and shared across any project—but may
          not be used to train other AI models.
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default StyleFilterDetailDialogContent;
